import React, { useCallback, useState, useMemo } from 'react';
import IconButton from '@material-ui/core/IconButton';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import Fade from '@material-ui/core/Fade';
import { makeStyles } from '@material-ui/core/styles';
import { TextInput } from '../UIkit';

const useStyles = makeStyles({
  iconCell: {
    padding: 0,
    height: 48,
    width: 48,
  },
  checkIcon: {
    float: 'right',
  },
  inputText: {
    display: 'flex',
  },
  outOfStockRow: {
    backgroundColor: 'rgba(244, 67, 54, 0.08)',
    position: 'relative',
  },
  outOfStockText: {
    textDecoration: 'line-through',
    color: 'rgba(0, 0, 0, 0.5)',
  },
  outOfStockChip: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    backgroundColor: '#f44336',
    color: 'white',
  },
  outOfStockCell: {
    position: 'relative',
  },
});

const SetSizeArea = (props) => {
  const classes = useStyles();
  const [index, setIndex] = useState(0),
    [size, setSize] = useState(''),
    [basePrice, setBasePrice] = useState(0),
    [editingIndex, setEditingIndex] = useState(-1);

  const inputSize = useCallback(
    (event) => {
      setSize(event.target.value);
    },
    [setSize]
  );

  const inputBasePrice = useCallback(
    (event) => {
      setBasePrice(event.target.value);
    },
    [setBasePrice]
  );

  const addSize = (index, size, basePrice) => {
    if (size === '' || basePrice === '') {
      return false;
    } else {
      if (index === props.sizes.length) {
        props.setSizes((prevState) => [
          ...prevState,
          { size: size, basePrice: basePrice },
        ]);
        setIndex(index + 1);
        setSize('');
        setBasePrice(0);
      } else {
        const newSizes = [...props.sizes]; // スプレッド構文で配列をコピー
        newSizes[index] = {
          ...newSizes[index], // 既存のプロパティを保持
          size: size,
          basePrice: basePrice,
        };
        props.setSizes(newSizes);
        setIndex(newSizes.length);
        setSize('');
        setBasePrice(0);
      }
      setEditingIndex(-1);
    }
  };

  const editSize = (index, size, basePrice) => {
    setIndex(index);
    setSize(size);
    setBasePrice(basePrice);
    setEditingIndex(index);
  };

  const deleteSize = (deleteIndex) => {
    const newSizes = props.sizes.filter((item, i) => i !== deleteIndex);
    props.setSizes(newSizes);
  };

  const toggleOutOfStock = (index) => {
    const newSizes = [...props.sizes]; // スプレッド構文で配列をコピー
    const currentItem = newSizes[index];

    // outOfStockプロパティが存在するかどうかをチェック
    if ('outOfStock' in currentItem) {
      // 既に存在する場合は値を反転
      newSizes[index] = {
        ...currentItem,
        outOfStock: !currentItem.outOfStock,
      };
    } else {
      // 存在しない場合は新たにtrueとして追加
      newSizes[index] = {
        ...currentItem,
        outOfStock: true,
      };
    }

    props.setSizes(newSizes);
  };

  // チェックボックスの状態を取得する関数
  const isOutOfStock = (item) => {
    return item.outOfStock === true;
  };

  useMemo(() => {
    setIndex(props.sizes.length);
  }, [props.sizes.length]);

  return (
    <div aria-label="サイズ展開">
      <TableContainer component={Paper}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>品切</TableCell>
              <TableCell>サイズ</TableCell>
              <TableCell>本体価格</TableCell>
              <TableCell className={classes.iconCell} />
              <TableCell className={classes.iconCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {props.sizes.length > 0 &&
              props.sizes.map((item, i) => (
                <React.Fragment key={i}>
                  <TableRow
                    className={isOutOfStock(item) ? classes.outOfStockRow : ''}
                  >
                    <TableCell className={classes.outOfStockCell}>
                      <Checkbox
                        checked={isOutOfStock(item)}
                        onChange={() => toggleOutOfStock(i)}
                        color="primary"
                      />
                      {isOutOfStock(item) && (
                        <Fade
                          in={true}
                          timeout={500}
                          style={{ marginTop: '-5px' }}
                        >
                          <Chip
                            style={{ fontSize: '12px', padding: '0px 2px' }}
                            label="品切れ"
                            size="small"
                            className={classes.outOfStockChip}
                          />
                        </Fade>
                      )}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      className={
                        isOutOfStock(item) ? classes.outOfStockText : ''
                      }
                    >
                      {item.size}
                    </TableCell>
                    <TableCell
                      className={
                        isOutOfStock(item) ? classes.outOfStockText : ''
                      }
                    >
                      {item.basePrice}
                    </TableCell>
                    <TableCell className={classes.iconCell}>
                      <IconButton
                        className={classes.iconCell}
                        onClick={() => editSize(i, item.size, item.basePrice)}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell className={classes.iconCell}>
                      <IconButton
                        className={classes.iconCell}
                        onClick={() => deleteSize(i)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {/* 編集中の行には入力フィールドを表示 */}
                  {editingIndex === i && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <div className={classes.inputText}>
                          <TextInput
                            fullWidth={false}
                            label={'サイズ'}
                            multiline={false}
                            required={true}
                            onChange={inputSize}
                            minRows={1}
                            value={size}
                            type={'text'}
                          />
                          <TextInput
                            fullWidth={false}
                            label={'本体価格'}
                            multiline={false}
                            required={true}
                            onChange={inputBasePrice}
                            minRows={1}
                            value={basePrice}
                            type={'number'}
                          />
                        </div>
                        <IconButton
                          className={classes.checkIcon}
                          onClick={() => addSize(index, size, basePrice)}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
        {/* 編集中の行がない場合、新しい入力フィールドを表示 */}
        {(editingIndex === -1 || editingIndex === props.sizes.length) && (
          <>
            <div className={classes.inputText}>
              <TextInput
                fullWidth={false}
                label={'サイズ'}
                multiline={false}
                required={true}
                onChange={inputSize}
                minRows={1}
                value={size}
                type={'text'}
              />
              <TextInput
                fullWidth={false}
                label={'本体価格'}
                multiline={false}
                required={true}
                onChange={inputBasePrice}
                minRows={1}
                value={basePrice}
                type={'number'}
              />
            </div>
            <IconButton
              className={classes.checkIcon}
              onClick={() => addSize(index, size, basePrice)}
            >
              <CheckCircleIcon />
            </IconButton>
          </>
        )}
      </TableContainer>
      <div className="module-spacer--small" />
    </div>
  );
};

export default SetSizeArea;
